import React from 'react';
import { Moon, Sun, Monitor } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

interface ThemeToggleProps {
  showLabel?: boolean;
  variant?: 'icon-only' | 'with-text' | 'dropdown';
  className?: string;
}

const ThemeToggle: React.FC<ThemeToggleProps> = ({ 
  showLabel = false, 
  variant = 'icon-only',
  className = '' 
}) => {
  const { theme, effectiveTheme, setTheme, toggleTheme } = useTheme();

  if (variant === 'dropdown') {
    return (
      <div className={`relative inline-block ${className}`}>
        <select
          value={theme}
          onChange={(e) => setTheme(e.target.value as 'light' | 'dark' | 'system')}
          className="appearance-none bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 pr-10 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors cursor-pointer"
        >
          <option value="light">Light</option>
          <option value="dark">Dark</option>
          <option value="system">System</option>
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 dark:text-gray-200">
          {theme === 'light' && <Sun className="h-4 w-4" />}
          {theme === 'dark' && <Moon className="h-4 w-4" />}
          {theme === 'system' && <Monitor className="h-4 w-4" />}
        </div>
      </div>
    );
  }

  if (variant === 'with-text') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <button
          onClick={toggleTheme}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label={`Switch to ${effectiveTheme === 'light' ? 'dark' : 'light'} mode`}
        >
          {effectiveTheme === 'light' ? (
            <>
              <Sun className="h-5 w-5" />
              {showLabel && <span className="text-sm font-medium">Light</span>}
            </>
          ) : (
            <>
              <Moon className="h-5 w-5" />
              {showLabel && <span className="text-sm font-medium">Dark</span>}
            </>
          )}
        </button>
      </div>
    );
  }

  // Default: icon-only variant - removed border and background
  return (
    <button
      onClick={toggleTheme}
      className={`text-gray-700 dark:text-gray-200 hover:text-primary dark:hover:text-primary transition-colors focus:outline-none ${className}`}
      aria-label={`Switch to ${effectiveTheme === 'light' ? 'dark' : 'light'} mode`}
      title={`Switch to ${effectiveTheme === 'light' ? 'dark' : 'light'} mode`}
    >
      {effectiveTheme === 'light' ? (
        <Sun className="h-5 w-5" />
      ) : (
        <Moon className="h-5 w-5" />
      )}
    </button>
  );
};

export default ThemeToggle;